// What a landing carries, on the phone (Poulpe-625), as the approved prototype
// draws it (mobile/ChangesScreen.tsx, Fabien's addition in Poulpe-622's
// record 21). From the approval, it shows each proposal's changed files, the
// server's kept copy (`GET /v1/proposals/{id}/changes`). That copy is read
// again shortly while the server is still asking the proposing runner for it,
// as the Board's cards do (board/useProposalCopies.ts). Then it shows one
// file's diff (`…/changes/diff`), drawn by the Changes pane's own `FileDiff`
// (code/ChangesPane.tsx). Seeing it is optional: the approval never waits
// for it.
import { useCallback, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import type { ChangedFile, Proposal, SessionChanges } from '../api/types'
import { useProposalCopies, type CopyState } from '../board/useProposalCopies'
import { FileDiff, useFileDiffs } from '../code/ChangesPane'
import { short } from '../queue/words'
import { useSessionsEnv } from '../sessions/context'
import { useBoardState } from '../store/context'
import { approvalOf, carriedIssues } from './needs'
import { Note, Screen, TopBar } from './shared'

const STATUS_WORDS: Record<string, string> = { A: 'Added', M: 'Modified', D: 'Deleted', R: 'Renamed', '?': 'Untracked' }

/** A file's status in words, and its class: a status the page does not know is "Changed", in the neutral class. */
function statusOf(status: string): { word: string; className: string } {
  const word = STATUS_WORDS[status]
  return word ? { word, className: `changes-status changes-status-${word.toLowerCase()}` } : { word: 'Changed', className: 'changes-status' }
}

/** The proposals of the approval a landing asks, or null once it asks none. */
function useSubjectProposals(landingId: string): readonly string[] | null {
  const { landings } = useBoardState()
  const landing = landings.get(landingId)
  const asked = landing ? approvalOf(landing) : null
  return asked ? asked.subject.proposals : null
}

/** A proposal by the keys of the issues it carries, else by its short id. */
function useProposalName(): (id: string, proposal: Proposal | undefined) => string {
  const { proposals, issues } = useBoardState()
  return (id, proposal) => {
    const keys = proposal ? carriedIssues([id], proposals, issues).map((issue) => issue.key) : []
    return keys.length > 0 ? keys.join(', ') : short(id)
  }
}

/** Each proposal's files, each a link to its diff. */
export function ChangesScreen() {
  const { id = '' } = useParams()
  const ids = useSubjectProposals(id)
  const copies = useProposalCopies(ids ?? [])
  const { proposals } = useBoardState()
  const nameOf = useProposalName()
  const back = `/approval/${encodeURIComponent(id)}`
  const top = <TopBar title="What it changes" back={back} backLabel="Approval" />
  if (!ids) return <Screen top={top}><Note>This approval is no longer asked: the landing went on or ended.</Note></Screen>
  return (
    <Screen top={top}>
      <p className="form-hint">Optional: the approval does not wait for this.</p>
      {ids.map((proposal) => (
        <section key={proposal} aria-label={`Proposal ${nameOf(proposal, proposals.get(proposal))}`}>
          <h2 className="request-title">{nameOf(proposal, proposals.get(proposal))}{proposals.get(proposal) && <> at <code>{short(proposals.get(proposal)!.head)}</code></>}</h2>
          <Copy landing={id} proposal={proposal} copy={copies.get(proposal)} />
        </section>
      ))}
      <Link className="button m-target" to={back}>Back to the approval</Link>
    </Screen>
  )
}

/** A copy not in yet: being read, being prepared, or why there is none. */
function CopyMissing({ copy }: { copy: CopyState | undefined }) {
  if (!copy) return <Note>Reading its changes…</Note>
  if (copy.preparing) return <Note>Its changes are being copied from its runner. Look again shortly.</Note>
  return <Note tone="question-error">Its changes could not be read: {copy.problem}</Note>
}

/** One proposal's kept copy: being read, being prepared, why there is none, or its files. */
function Copy({ landing, proposal, copy }: { landing: string; proposal: string; copy: CopyState | undefined }) {
  if (!copy?.changes) return <CopyMissing copy={copy} />
  const { files, base, truncated } = copy.changes
  return (
    <>
      <p className="muted">{files.length} {files.length === 1 ? 'file' : 'files'}{base && <>, against its base <code>{short(base)}</code></>}</p>
      {files.length > 0 && (
        // The Changes pane's rows (ChangedFiles, code/ChangesPane.tsx), in its classes; each leads to its diff.
        <ul className="changes-list m-files" aria-label="Changed files">
          {files.map((file) => {
            const slash = file.path.lastIndexOf('/')
            const status = statusOf(file.status)
            return (
              <li key={file.path} className="changes-file">
                <Link className="changes-file-row m-target" to={`/approval/${encodeURIComponent(landing)}/changes/${encodeURIComponent(proposal)}/file?path=${encodeURIComponent(file.path)}`}>
                  <span className={status.className} aria-hidden="true">{file.status}</span>
                  <span className="changes-path">{slash >= 0 && <span className="changes-dir">{file.path.slice(0, slash + 1)}</span>}<span className="changes-name">{file.path.slice(slash + 1)}</span></span>{' '}
                  <span className="m-sr">{status.word}{file.old_path ? ` from ${file.old_path}` : ''} · {file.binary ? 'binary' : `${file.additions ?? '?'} added, ${file.deletions ?? '?'} removed`}</span>
                  <span className="changes-counts" aria-hidden="true">{file.binary ? <span className="changes-binary">binary</span> : <>{file.additions !== null && <span className="changes-added">+{file.additions}</span>}{' '}{file.deletions !== null && <span className="changes-removed">−{file.deletions}</span>}</>}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
      {truncated && <p className="changes-truncated" role="status">More files changed than are listed here.</p>}
    </>
  )
}

/** One file's diff, in the Changes pane's renderer. */
export function FileScreen() {
  const { id = '', proposal = '' } = useParams()
  const [params] = useSearchParams()
  const path = params.get('path') ?? ''
  const ids = useSubjectProposals(id)
  const asked = ids?.includes(proposal) ?? false
  const copy = useProposalCopies(asked ? [proposal] : []).get(proposal)
  const approval = `/approval/${encodeURIComponent(id)}`
  const top = <TopBar title="Diff" back={`${approval}/changes`} backLabel="Changes" />
  if (!ids) return <Screen top={top}><Note>This approval is no longer asked: the landing went on or ended.</Note></Screen>
  if (!asked) return <Screen top={top}><Note>This proposal is not in the approval asked now.</Note></Screen>
  if (!copy?.changes) return <Screen top={top}><CopyMissing copy={copy} /></Screen>
  const file = copy.changes.files.find((f) => f.path === path)
  if (!file) return <Screen top={top}><Note>This file is not in the proposal's kept copy.</Note></Screen>
  return (
    <Screen top={top}>
      <h2 className="request-title m-file-path">{file.path}</h2>
      <p className="muted">
        {statusOf(file.status).word}{file.old_path ? ` from ${file.old_path}` : ''}
        {!file.binary && file.additions !== null && ` · ${file.additions} added, ${file.deletions ?? 0} removed`}
      </p>
      <p className="form-hint">Added lines start with +, removed lines with −. Long lines scroll sideways.</p>
      <div className="m-diff">
        <Diff proposal={proposal} changes={copy.changes} file={file} />
      </div>
      <Link className="button m-target" to={approval}>Back to the approval</Link>
    </Screen>
  )
}

/** The file's diff, read once the copy that lists it is in: the pane's hook closes a file its report does not list. */
function Diff({ proposal, changes, file }: { proposal: string; changes: SessionChanges; file: ChangedFile }) {
  const { api } = useSessionsEnv()
  const { me } = useBoardState()
  const as = me?.member?.id ?? null
  const read = useCallback((path: string) => api.proposalChangesDiff(proposal, path, as), [api, proposal, as])
  const view = useFileDiffs(changes, read, [file.path])
  const [unfolded, setUnfolded] = useState(false)
  return <FileDiff file={file} state={view.diffs.get(file.path)} unfolded={unfolded} onUnfold={() => setUnfolded(true)} onRetry={() => view.retry(file.path)} />
}
